import { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE } from '../api/client';

export interface QueuedCheckin {
  id: string;
  registrationId: string;
  guestName: string;
  ticketNumber: string | null;
  pickupLocation: string;
  pin: string;
  queuedAt: number;
}

interface EnqueueInput {
  registrationId: string;
  guestName: string;
  ticketNumber?: string | null;
  pickupLocation: string;
  pin: string;
}

interface UseOfflineCheckinQueueResult {
  /** Add a check-in to the offline queue (deduped by registrationId). Returns false if already queued. */
  enqueueCheckin: (input: EnqueueInput) => boolean;
  /** Pending (not-yet-synced) check-ins. */
  pending: QueuedCheckin[];
  /** True when the browser reports a live network connection. */
  isOnline: boolean;
  /** True while the queue is actively replaying to the server. */
  syncing: boolean;
  /** Is this registration currently sitting in the queue? */
  isQueued: (registrationId: string) => boolean;
  /** Force a replay attempt now (e.g. user taps "Sync now"). */
  flush: () => void;
  /** Remove a stuck/unwanted item from the queue by its id. */
  removeCheckin: (id: string) => void;
}

const QUEUE_KEY = 'kotoku_checkin_queue';
const BASE_RETRY_MS = 1500;
const MAX_RETRY_MS = 30000;

function loadQueue(): QueuedCheckin[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(items: QueuedCheckin[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    // Ignore quota / serialization errors — worst case the in-memory queue still works this session.
  }
}

/**
 * Offline check-in queue for the desktop check-in station.
 *
 * Persists pending check-ins to localStorage so a page reload during a wifi
 * outage does not lose them. When connectivity returns (or on manual flush),
 * it replays each queued check-in to POST /guest-directory/check-in. That
 * endpoint is safe to replay: checkInGuest() returns success if the guest is
 * already checked in, so re-sending a check-in never double-counts or errors.
 */
export function useOfflineCheckinQueue(): UseOfflineCheckinQueueResult {
  const [pending, setPending] = useState<QueuedCheckin[]>(() => loadQueue());
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  const processingRef = useRef(false);
  const retryTimeoutRef = useRef<number | null>(null);
  const retryDelayRef = useRef(BASE_RETRY_MS);

  // Persist to localStorage whenever the queue changes.
  useEffect(() => {
    saveQueue(pending);
  }, [pending]);

  // Track connectivity.
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const sendCheckin = useCallback(async (item: QueuedCheckin): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/guest-directory/check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationId: item.registrationId,
          pickupLocation: item.pickupLocation,
          pin: item.pin,
        }),
      });
      if (!res.ok) return false;
      const result = await res.json();
      // A bad PIN comes back {success:false}. Treat that as NOT sent so the item
      // stays queued and the operator can see it isn't clearing (rather than
      // silently dropping a real check-in).
      return result?.success !== false;
    } catch {
      return false;
    }
  }, []);

  const scheduleRetry = useRef<(delay: number) => void>(() => {});

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    if (!navigator.onLine) return;

    let snapshot: QueuedCheckin[] = [];
    setPending((current) => {
      snapshot = current;
      return current;
    });
    if (snapshot.length === 0) return;

    processingRef.current = true;
    setSyncing(true);

    const next = snapshot[0];
    const success = await sendCheckin(next);

    if (success) {
      retryDelayRef.current = BASE_RETRY_MS;
      let remainingCount = 0;
      setPending((prev) => {
        const remaining = prev.filter((c) => c.id !== next.id);
        remainingCount = remaining.length;
        return remaining;
      });
      processingRef.current = false;
      if (remainingCount > 0 && navigator.onLine) {
        scheduleRetry.current(BASE_RETRY_MS);
      } else {
        setSyncing(false);
      }
    } else {
      processingRef.current = false;
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_MS);
      if (navigator.onLine) {
        scheduleRetry.current(retryDelayRef.current);
      } else {
        setSyncing(false);
      }
    }
  }, [sendCheckin]);

  // Wire scheduleRetry to processQueue (ref indirection avoids a definition cycle).
  useEffect(() => {
    scheduleRetry.current = (delay: number) => {
      if (retryTimeoutRef.current !== null) window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = window.setTimeout(() => {
        retryTimeoutRef.current = null;
        processQueue();
      }, delay);
    };
  }, [processQueue]);

  // Kick off processing when we come online or new items are queued.
  useEffect(() => {
    if (isOnline && pending.length > 0) {
      retryDelayRef.current = BASE_RETRY_MS;
      processQueue();
    }
    if (pending.length === 0) setSyncing(false);
  }, [isOnline, pending.length, processQueue]);

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current !== null) window.clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const enqueueCheckin = useCallback((input: EnqueueInput): boolean => {
    let added = true;
    setPending((prev) => {
      if (prev.some((c) => c.registrationId === input.registrationId)) {
        added = false;
        return prev;
      }
      const item: QueuedCheckin = {
        id: `chk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        registrationId: input.registrationId,
        guestName: input.guestName,
        ticketNumber: input.ticketNumber ?? null,
        pickupLocation: input.pickupLocation,
        pin: input.pin,
        queuedAt: Date.now(),
      };
      return [...prev, item];
    });
    return added;
  }, []);

  const isQueued = useCallback(
    (registrationId: string) => pending.some((c) => c.registrationId === registrationId),
    [pending],
  );

  const flush = useCallback(() => {
    retryDelayRef.current = BASE_RETRY_MS;
    processQueue();
  }, [processQueue]);

  const removeCheckin = useCallback((id: string) => {
    setPending((prev) => prev.filter((c) => c.id !== id));
  }, []);

  return { enqueueCheckin, pending, isOnline, syncing, isQueued, flush, removeCheckin };
}
